/*
 * Alpine component for the free tools at /tools/{slug}.
 *
 * run() posts the form to /tools/{slug}/run. The server answers with the
 * result, asks for an email (401 email_required: the gate modal sends and
 * checks a 6-digit code, then the run is retried), or reports the daily limit
 * (429 limit_reached: the upgrade prompt opens).
 *
 * Browser tools (BPM and key finder) get {status: 'pending', run} instead,
 * analyse the audio locally and post the result back.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const UTM_STORAGE = 'tokchart_tools_utm';

function rememberUtm() {
    const params = new URLSearchParams(window.location.search);
    const found = Object.fromEntries(UTM_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k).slice(0, 191)]));
    try {
        if (Object.keys(found).length) {
            sessionStorage.setItem(UTM_STORAGE, JSON.stringify(found));
            return found;
        }
        return JSON.parse(sessionStorage.getItem(UTM_STORAGE) || '{}');
    } catch {
        return found;
    }
}

export const money = (value) =>
    '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const moneyRange = (low, high) => (Number(low) === Number(high) ? money(low) : `${money(low)} – ${money(high)}`);

export const number = (value) => Number(value || 0).toLocaleString('en-US');

export const rate = (value) => '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 });

// Heatmap shades by level 0-4 (best time to post); keep in sync with its result view.
const HEAT_SHADES = ['bg-brand-100', 'bg-brand-200', 'bg-brand-400', 'bg-brand-600', 'bg-brand-800'];
const HOURS = ['12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
    '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM'];

export default function toolRunner(config) {
    return {
        slug: config.slug,
        csrf: config.csrf,
        urls: config.urls,
        form: { ...config.defaults },
        result: config.sample,
        isSample: true,
        busy: false,
        status: null,
        errors: {},
        remaining: null,
        cached: false,
        copied: false,

        gateOpen: false,
        gateStep: 'email',
        email: '',
        marketingConsent: false,
        code: '',
        gateBusy: false,
        gateErrors: {},
        gateMessage: null,

        limitOpen: false,
        limit: null,
        limitInfo: null, // the 429 body: limit, lane, resets_at (the new design shows it in the result card)
        upgradeUrl: config.urls.upgrade,

        utm: {},

        money,
        moneyRange,
        number,
        rate,

        init() {
            this.utm = rememberUtm();
        },

        /** Input sent to the server; browser tools override this. */
        payload() {
            return this.form;
        },

        async run() {
            if (this.busy) return;
            this.busy = true;
            this.errors = {};
            this.status = null;
            try {
                await this.submitRun(this.payload());
            } finally {
                this.busy = false;
            }
        },

        async submitRun(payload) {
            this.limitInfo = null;
            const { response, data } = await this.post(this.urls.run, payload);

            if (response.status === 401 && data.status === 'email_required') {
                this.openGate();
                return null;
            }
            if (response.status === 429 && data.status === 'limit_reached') {
                this.limit = data.limit;
                this.limitInfo = data;
                this.upgradeUrl = data.upgrade_url || this.upgradeUrl;
                this.limitOpen = true;
                return null;
            }
            if (response.status === 422) {
                this.errors = data.errors || {};
                return null;
            }
            if (!response.ok) {
                this.status = response.status === 503
                    ? 'We couldn\'t do that right now. It didn\'t count towards your free runs; please try again in a few minutes.'
                    : 'Something went wrong. Please try again.';
                return null;
            }

            this.remaining = data.remaining ?? null;
            // Queued lookups (and their cached answers) live on their own status page.
            if (data.redirect) {
                this.followLookup(data);
                return data;
            }
            if (data.status === 'ok') {
                this.showResult(data.result, data.cached);
            }

            return data;
        },

        /** A lookup was queued (or answered from cache): open its status page. The new design follows it in place. */
        followLookup(data) {
            window.location.href = data.redirect;
        },

        /** The email was verified (the new design shows it and remembers it for the lock line). */
        verifiedEmail() {},

        showResult(result, cached = false) {
            this.result = result;
            this.isSample = false;
            this.cached = cached;
            this.$nextTick(() => this.$refs.result?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
        },

        error(field) {
            return (this.errors[field] || [])[0] || null;
        },

        cellClass(day, hour) {
            const cell = this.result?.rows?.[day]?.[hour];
            return cell && cell.level !== null && cell.level !== undefined ? HEAT_SHADES[cell.level] : 'bg-white ring-1 ring-inset ring-slate-200';
        },

        cellTitle(day, hour, dayName) {
            const cell = this.result?.rows?.[day]?.[hour];
            const when = `${dayName} ${HOURS[hour]}`;
            if (!cell || cell.score === null) return `${when}: not enough videos`;
            return `${when}: ${cell.score.toFixed(2)}x usual views (${number(cell.videos)} videos)`;
        },

        async copy(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.copied = true;
                setTimeout(() => (this.copied = false), 2000);
            } catch {
                this.copied = false;
            }
        },

        // ------------------------------------------------------- email gate

        openGate() {
            this.gateStep = 'email';
            this.gateErrors = {};
            this.gateMessage = null;
            this.code = '';
            this.gateOpen = true;
        },

        async sendCode() {
            if (this.gateBusy) return;
            this.gateBusy = true;
            this.gateErrors = {};
            this.gateMessage = null;
            try {
                const { response, data } = await this.post(this.urls.send, { email: this.email, tool: this.slug, marketing_consent: this.marketingConsent, ...this.utm });
                if (response.ok) {
                    this.gateStep = 'code';
                    this.$nextTick(() => this.$refs.code?.focus());
                } else if (response.status === 422) {
                    this.gateErrors = data.errors || {};
                } else if (response.status === 429) {
                    this.gateMessage = data.message || 'Too many attempts. Please wait a few minutes and try again.';
                    if (data.status === 'wait') this.gateStep = 'code';
                } else {
                    this.gateMessage = 'We couldn\'t send the code. Please try again.';
                }
            } finally {
                this.gateBusy = false;
            }
        },

        async confirmCode() {
            if (this.gateBusy) return;
            this.gateBusy = true;
            this.gateErrors = {};
            this.gateMessage = null;
            try {
                const { response, data } = await this.post(this.urls.confirm, { email: this.email, code: this.code.trim() });
                if (response.ok) {
                    this.gateOpen = false;
                    this.verifiedEmail();
                    await this.run();
                } else if (response.status === 422) {
                    this.gateErrors = data.errors || {};
                } else {
                    this.gateMessage = 'Too many attempts. Please wait a few minutes and try again.';
                }
            } finally {
                this.gateBusy = false;
            }
        },

        gateError(field) {
            return (this.gateErrors[field] || [])[0] || null;
        },

        // ------------------------------------------------------- http

        async post(url, body) {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(body),
            });
            let data = {};
            try {
                data = await response.json();
            } catch {
                // Non-JSON error page.
            }

            return { response, data };
        },
    };
}

/** BPM and key finder: file upload or microphone, analysed in the browser. */
export function bpmKeyFinder(config) {
    const base = toolRunner(config);

    return {
        ...base,
        audio: null, // { bytes: ArrayBuffer, name, source }
        recording: false,
        recorder: null,
        recordSeconds: 0,
        recordTimer: null,
        phase: null, // 'reading' | 'analysing'
        audioError: null,

        payload() {
            return null;
        },

        chooseFile(event) {
            const file = event.target.files?.[0];
            this.audioError = null;
            if (!file) return;
            file.arrayBuffer().then((bytes) => {
                this.audio = { bytes, name: file.name, source: 'file' };
            });
        },

        async toggleRecording() {
            if (this.recording) {
                this.recorder?.stop();
                return;
            }
            this.audioError = null;
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch {
                this.audioError = 'We couldn\'t use your microphone. Check your browser\'s permission and try again.';
                return;
            }
            const chunks = [];
            this.recorder = new MediaRecorder(stream);
            this.recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
            this.recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                clearInterval(this.recordTimer);
                this.recording = false;
                const blob = new Blob(chunks, { type: this.recorder.mimeType });
                this.audio = { bytes: await blob.arrayBuffer(), name: `Recording (${this.recordSeconds}s)`, source: 'microphone' };
            };
            this.recordSeconds = 0;
            this.recording = true;
            this.recorder.start();
            this.recordTimer = setInterval(() => {
                this.recordSeconds++;
                if (this.recordSeconds >= 30) this.recorder.stop();
            }, 1000);
        },

        async run() {
            if (this.busy) return;
            if (!this.audio) {
                this.audioError = 'Choose an audio file or record one first.';
                return;
            }
            this.busy = true;
            this.errors = {};
            this.status = null;
            this.audioError = null;
            try {
                this.phase = 'reading';
                const analysis = await import('./audio-analysis.js');
                let decoded;
                try {
                    decoded = await analysis.decodeToMono(this.audio.bytes);
                } catch {
                    this.audioError = 'We couldn\'t read this audio. Try an MP3, WAV or M4A file.';
                    return;
                }
                const digest = await crypto.subtle.digest('SHA-256', this.audio.bytes);
                const sha = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');

                const data = await this.submitRun({
                    audio_sha256: sha,
                    source: this.audio.source,
                    duration: Math.max(1, Math.round(decoded.duration * 10) / 10),
                });
                if (data?.status !== 'pending') return;

                this.phase = 'analysing';
                await new Promise((r) => setTimeout(r, 30)); // let the UI paint
                const result = analysis.analyse(decoded.samples, decoded.sampleRate);
                const stored = {
                    bpm: result.bpm,
                    key: result.key,
                    scale: result.scale,
                    confidence: result.confidence,
                    second_key: result.second_key,
                    second_scale: result.second_scale,
                };
                this.showResult(stored);
                await this.post(this.urls.complete.replace('__RUN__', data.run), stored);
            } finally {
                this.phase = null;
                this.busy = false;
            }
        },

        relatedKeys(result) {
            return relatedKeys(result.key, result.scale);
        },
    };
}

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Keys that mix well: relative major/minor and the neighbours on the circle of fifths. */
export function relatedKeys(key, scale) {
    const i = NOTES.indexOf(key);
    if (i < 0) return [];
    const name = (n, s) => `${NOTES[((n % 12) + 12) % 12]} ${s}`;
    const relative = scale === 'minor' ? name(i + 3, 'major') : name(i - 3, 'minor');

    return [relative, name(i + 7, scale), name(i + 5, scale)];
}

/** A link being typed or pasted ("htt…", "vm.tiktok.com/…"): not a song search. */
const looksLikeLink = (q) => {
    const lower = q.toLowerCase();
    return 'https://'.startsWith(lower) || 'http://'.startsWith(lower)
        || /^(https?:\/\/|www\.|vm\.|vt\.|m\.tiktok)/.test(lower) || lower.includes('tiktok.com');
};

/** Song genre finder: the normal runner plus a song picker (stored sounds, /tools/song-genre-finder/search). */
export function genreFinder(config) {
    const base = toolRunner(config);

    return {
        ...base,
        sounds: [],
        searchOpen: false,
        searchSeq: 0,
        searchEmpty: false,

        payload() {
            return { query: this.form.query, sound_id: this.form.sound_id || null };
        },

        titleCase(text) {
            return String(text || '').replace(/\b\w/g, (c) => c.toUpperCase());
        },

        async search() {
            // Typing again drops the picked song.
            this.form.sound_id = '';
            const q = (this.form.query || '').trim();
            this.searchEmpty = false;
            if (q.length < 2 || looksLikeLink(q) || !this.urls.search) {
                this.searchSeq++; // drop answers to earlier searches
                this.sounds = [];
                this.searchOpen = false;
                return;
            }
            const seq = ++this.searchSeq;
            try {
                const response = await fetch(`${this.urls.search}?q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } });
                const data = response.ok ? await response.json() : { sounds: [] };
                if (seq !== this.searchSeq) return; // a newer search is on its way
                this.sounds = data.sounds || [];
                this.searchOpen = this.sounds.length > 0;
                this.searchEmpty = this.sounds.length === 0;
            } catch {
                this.sounds = [];
                this.searchOpen = false;
            }
        },

        pick(sound) {
            this.form.query = sound.author ? `${sound.title} – ${sound.author}` : sound.title;
            this.form.sound_id = sound.id;
            this.searchOpen = false;
        },
    };
}

/** Follower tracker: formatting and an SVG line for result.history (used on the tool and status pages). */
export function followerStats() {
    const fmt = (v) => Number(v || 0).toLocaleString('en-US');
    const points = (history) => {
        const values = history.map((d) => d.followers);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const span = max - min || 1;
        return values.map((v, i) => [(i / Math.max(1, values.length - 1)) * 600, 150 - ((v - min) / span) * 140]);
    };

    return {
        fmt,
        signed: (v) => (v === null || v === undefined ? '' : (v > 0 ? '+' : v < 0 ? '−' : '') + fmt(Math.abs(v))),
        tone: (v) => (v > 0 ? 'text-success' : v < 0 ? 'text-danger' : 'text-muted'),
        line(history) {
            return points(history).map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
        },
        area(history) {
            const p = points(history);
            return `M0,160 ${p.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(' ')} L600,160 Z`;
        },
        /** The last `n` days, newest first, with the change from the day before. */
        recent(history, n) {
            return history.map((d, i) => ({ ...d, change: i ? d.followers - history[i - 1].followers : null })).slice(-n).reverse();
        },
    };
}

const sha256Hex = async (bytes) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
    .map((b) => b.toString(16).padStart(2, '0')).join('');

const mmss = (s) => {
    const m = Math.floor(s / 60);
    const sec = (s - m * 60).toFixed(1).padStart(4, '0');
    return `${m}:${sec}`;
};

/**
 * MP3 cutter and pitch changer: the file is decoded and processed in the browser
 * (audio-tools.js). The server gates and counts the run; we post back a summary.
 */
export function audioTool(config) {
    const base = toolRunner(config);
    const presets = config.presets || {};

    return {
        ...base,
        file: null, // { bytes, name, sha }
        buffer: null,
        waveform: [],
        phase: null, // 'reading' | 'processing'
        audioError: null,
        output: null, // { url, name, seconds }
        playing: null,
        mmss,

        payload() {
            return null;
        },

        async chooseFile(event) {
            const f = event.target.files?.[0];
            this.audioError = null;
            this.output = null;
            if (!f) return;
            this.phase = 'reading';
            try {
                const bytes = await f.arrayBuffer();
                const tools = await import('./audio-tools.js');
                this.buffer = await tools.decodeAudio(bytes);
                this.waveform = tools.peaks(this.buffer, 300);
                this.file = { bytes, name: f.name.replace(/\.[^.]+$/, ''), sha: await sha256Hex(bytes) };
                if (this.slug === 'mp3-cutter') {
                    this.form.start = 0;
                    this.form.end = Math.round(Math.min(this.buffer.duration, 30) * 10) / 10;
                }
            } catch {
                this.buffer = null;
                this.audioError = 'We couldn\'t read this audio. Try an MP3, WAV or M4A file.';
            } finally {
                this.phase = null;
            }
        },

        applyPreset(key) {
            const p = presets[key];
            if (!p) return;
            Object.assign(this.form, { preset: key, semitones: p.semitones, tempo: p.tempo, speed: p.speed, reverb: p.reverb });
        },

        settings() {
            const f = this.form;
            if (this.slug === 'mp3-cutter') {
                return { start: Number(f.start) || 0, end: Number(f.end) || 0, fade_in: Number(f.fade_in) || 0, fade_out: Number(f.fade_out) || 0, format: f.format };
            }
            return { preset: f.preset, semitones: Number(f.semitones) || 0, tempo: Number(f.tempo) || 1, speed: Number(f.speed) || 1, reverb: Number(f.reverb) || 0 };
        },

        async render(tools) {
            const s = this.settings();
            if (this.slug === 'mp3-cutter') {
                const cut = tools.trim(this.buffer, s.start, s.end, s.fade_in, s.fade_out);
                const blob = s.format === 'wav' ? tools.encodeWav(cut) : await tools.encodeMp3(cut, 192);
                return { blob, seconds: cut.duration, name: `${this.file.name} (cut).${s.format}`, format: s.format };
            }
            const out = await tools.transform(this.buffer, { semitones: s.semitones, tempo: s.tempo, speed: s.speed, reverb: s.reverb });
            const label = presets[s.preset]?.name && s.preset !== 'custom' ? presets[s.preset].name.toLowerCase() : 'edited';
            return { blob: await tools.encodeMp3(out, 192), seconds: out.duration, name: `${this.file.name} (${label}).mp3`, format: 'mp3' };
        },

        async preview() {
            if (!this.buffer) return;
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const src = ctx.createBufferSource();
            const s = this.settings();
            src.buffer = this.buffer;
            src.connect(ctx.destination);
            if (this.slug === 'mp3-cutter') {
                src.start(0, s.start, Math.max(0.1, s.end - s.start));
            } else {
                src.playbackRate.value = s.speed;
                src.start(0, 0, 15);
            }
            this.playing = { ctx, src };
            src.onended = () => {
                ctx.close();
                this.playing = null;
            };
        },

        stopPreview() {
            this.playing?.src.stop();
        },

        async run() {
            if (this.busy) return;
            if (!this.buffer) {
                this.audioError = 'Choose an audio file first.';
                return;
            }
            this.busy = true;
            this.errors = {};
            this.status = null;
            this.audioError = null;
            try {
                const data = await this.submitRun({ audio_sha256: this.file.sha, duration: Math.max(0.5, Math.round(this.buffer.duration * 10) / 10), ...this.settings() });
                // pending: a new run; ok: the same edit was done before (not counted), render it again.
                if (data?.status !== 'pending' && data?.status !== 'ok') return;
                this.phase = 'processing';
                await new Promise((r) => setTimeout(r, 30));
                const tools = await import('./audio-tools.js');
                const done = await this.render(tools);
                if (this.output?.url) URL.revokeObjectURL(this.output.url);
                this.output = { url: URL.createObjectURL(done.blob), name: done.name, seconds: done.seconds };
                const summary = { seconds: Math.round(done.seconds * 10) / 10, bytes: done.blob.size, ...(this.slug === 'mp3-cutter' ? { format: done.format } : {}) };
                this.showResult(summary);
                if (data.status === 'pending') await this.post(this.urls.complete.replace('__RUN__', data.run), summary);
            } catch {
                this.audioError = 'Something went wrong while processing the audio. Try a shorter or different file.';
            } finally {
                this.phase = null;
                this.busy = false;
            }
        },
    };
}

/** Vocal range test: live pitch detection from the microphone; nothing is recorded. */
export function vocalRange(config) {
    const base = toolRunner(config);

    return {
        ...base,
        listening: false,
        current: null, // { midi, name }
        low: null,
        high: null,
        micError: null,
        stream: null,
        ctx: null,
        frame: null,
        noteName: (m) => (m === null ? '—' : `${['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][m % 12]}${Math.floor(m / 12) - 1}`),

        payload() {
            return null;
        },

        async run() {
            if (this.busy || this.listening) return;
            this.micError = null;
            this.busy = true;
            try {
                const testId = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
                const data = await this.submitRun({ test_id: testId });
                if (data?.status !== 'pending') return;
                this.runId = data.run;
                await this.listen();
            } finally {
                this.busy = false;
            }
        },

        async listen() {
            const tools = await import('./audio-tools.js');
            try {
                this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
            } catch {
                this.micError = 'We couldn\'t use your microphone. Check your browser\'s permission and try again.';
                return;
            }
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = this.ctx.createAnalyser();
            analyser.fftSize = 2048;
            this.ctx.createMediaStreamSource(this.stream).connect(analyser);
            const buf = new Float32Array(analyser.fftSize);
            this.low = null;
            this.high = null;
            this.listening = true;
            const recent = [];
            const tick = () => {
                if (!this.listening) return;
                analyser.getFloatTimeDomainData(buf);
                const hz = tools.detectPitch(buf, this.ctx.sampleRate);
                const midi = hz ? tools.midiOf(hz) : null;
                recent.push(midi);
                if (recent.length > 12) recent.shift();
                // A note counts once it is held: the same note in 9 of the last 12 frames (~0.3 s).
                const held = midi !== null && recent.filter((m) => m === midi).length >= 9;
                this.current = midi !== null ? { midi, name: tools.noteName(midi) } : null;
                if (held && midi >= 28 && midi <= 96) {
                    this.low = this.low === null ? midi : Math.min(this.low, midi);
                    this.high = this.high === null ? midi : Math.max(this.high, midi);
                }
                this.frame = requestAnimationFrame(tick);
            };
            tick();
        },

        async finish() {
            this.listening = false;
            cancelAnimationFrame(this.frame);
            this.stream?.getTracks().forEach((t) => t.stop());
            this.ctx?.close();
            if (this.low === null || this.high === null) {
                this.micError = 'We didn\'t hear a clear note. Sing a little louder and hold each note for a second.';
                return;
            }
            const tools = await import('./audio-tools.js');
            const result = { low: this.low, high: this.high, voice_type: tools.voiceType(this.low, this.high) };
            this.showResult(result);
            if (this.runId) await this.post(this.urls.complete.replace('__RUN__', this.runId), result);
        },

        octaves(r) {
            return r ? ((r.high - r.low) / 12).toFixed(1) : '';
        },
    };
}
